<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">{{ t("occupation.title") }}</h1>
      <div class="text-light-gray text-sm">
        {{ t("occupation.subTitle") }}
      </div>
    </div>
    <div v-if="isLoading" class="flex h-[60vh] justify-center">
      <LoadingIcon :isLoading="isLoading" />
    </div>
    <div class="flex flex-col gap-4" v-if="!isLoading">
      <div class="grid grid-cols-3 gap-3 my-4">
        <InfoBox
          :title="t('occupation.states.usedResources')"
          :value="occupancyDashboardData!.usedResources.value"
          :backgroundColor="EventState.events[5].lightColor"
          :textColor="EventState.events[5].color"
        >
          <IconManager
            name="Box"
            :style="{ color: EventState.events[5].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('occupation.states.occupation')"
          :value="occupancyDashboardData!.occupation.value + '%'"
          :backgroundColor="EventState.events[4].lightColor"
          :textColor="EventState.events[4].color"
        >
          <IconManager
            name="Percentage"
            :style="{ color: EventState.events[4].color }"
          />
        </InfoBox>

        <InfoBox
          :title="t('occupation.states.averageAssembly')"
          :value="
            occupancyDashboardData!.averageAssembly.value +
            t('common.hourAcronym')
          "
          :backgroundColor="EventState.events[2].lightColor"
          :textColor="EventState.events[2].color"
        >
          <IconManager
            name="Clock"
            :style="{ color: EventState.events[2].color }"
          />
        </InfoBox>
      </div>
      <FilterBox styleInputOptions="md:grid-cols-3">
        <template #inputOptions>
          <CustomInput
            v-model="filterResourceId"
            maxlength="50"
            :title="t('occupation.filter.resourceId')"
            :placeholder="t('occupation.filter.resourceId')"
          >
            <IconManager name="Search" class="text-light-gray" />
          </CustomInput>

          <CustomDropDown
            :title="t('occupation.filter.eventType')"
            :data="
              eventTypes.map((item, index) => {
                return { id: index, value: item };
              })
            "
            dataKey="id"
            dataValue="value"
            v-model="filterEventType"
          />

          <CustomInput
            v-model="filterLocation"
            maxlength="50"
            :title="t('occupation.filter.location')"
            :placeholder="t('occupation.filter.location')"
          >
            <IconManager name="Search" class="text-light-gray" />
          </CustomInput>

          <CustomDatePicker
            :title="t('occupation.filter.startDate')"
            v-model="filterStartDate"
            :clearable="true"
            :placeholder="t('occupation.filter.startDate')"
          />

          <CustomDatePicker
            :title="t('occupation.filter.endDate')"
            :placeholder="t('occupation.filter.endDate')"
            v-model="filterEndDate"
            :format-locale="pt"
            :clearable="true"
            :minDate="filterStartDate"
          />
        </template>
        <template #filterStateOptions>
          <FilterState
            v-for="option in eventStates"
            :label="option.getLocalization()"
            :value="option.value"
            :color="option.color"
            v-model="filterState"
          />
        </template>
        <template #filterButtons>
          <div class="flex flex-row sm:w-[35%] sm:ml-auto gap-2">
            <CustomCleanFilterButton
              class="flex-1"
              :title="t('common.filter.cleanFilters')"
              @click="clearFilters"
              :numberActivedFilters="getActiveFiltersCount()"
              :hasActiveFilters="hasActiveFilters()"
            />

            <CustomApplyFilterButton
              class="flex-1"
              :title="t('common.filter.filter')"
              @click="applyFilters"
              :hasUnappliedFilters="hasUnappliedFilters()"
            />
          </div>
        </template>
        <template #filterTagBox>
          <AppliedFilterBox :hasActiveFilters="hasActiveFilters()">
            <template #filterTag>
              <FilterTag
                :title="t('events.list.filter.id')"
                :value="appliedFilters.resourceId"
                :isVisible="filterResourceId"
              />

              <FilterTag
                :title="t('events.list.filter.name')"
                :value="appliedFilters.eventType"
                :isVisible="filterEventType"
              />

              <FilterTag
                :title="t('events.list.filter.location')"
                :value="appliedFilters.location"
                :isVisible="filterLocation"
              />

              <FilterTag
                :title="t('events.list.filter.startDate')"
                :value="appliedFilters.startDate"
                :isVisible="filterStartDate"
              />

              <FilterTag
                :title="t('events.list.filter.endDate')"
                :value="appliedFilters.endDate"
                :isVisible="filterEndDate"
              />

              <FilterTag
                :title="t('audit.filter.state')"
                :value="
                  appliedFilters.state.map((item) => item.getLocalization())
                "
                :isVisible="filterState.length > 0"
              />
            </template>
          </AppliedFilterBox>
        </template>
      </FilterBox>
      <div class="flex flex-row gap-4">
        <div class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full">
          <div class="flex flex-row w-full justify-between">
            <span class="font-extrabold">
              {{ t("occupation.timeline.title") }}
            </span>
          </div>
          <TimelineChart
            :events="
              occupancyDashboardData!.resources.map((item) => ({
                id: item.resourceId,
                title: item.name,
                events: item.intervals.flatMap((interval) =>
                  interval.bars.map((bar) => ({
                    id: interval.eventId,
                    startDate: bar.startDate,
                    endDate: bar.endDate,
                    name: interval.name,
                    color: Utils.getRandomColor(),
                    tooltipContent: buildTooltipHtml(
                      interval.eventId,
                      interval.quantity,
                      new Date(bar.startDate),
                      new Date(bar.endDate),
                    ),
                  })),
                ),
              }))
            "
            :callBack="(id: string) => go(RouteName.ResourceDetail, { id: id })"
          >
            <template #tooltip="{ bar }">
              <strong>{{ bar.name }}</strong>
              <div>{{ bar.extra?.location }}</div>
              <div>
                {{ bar.extra?.participants }}
                {{ t("occupation.timeline.tooltip.participants") }}
              </div>
            </template>
          </TimelineChart>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import { useI18n } from "vue-i18n";
import TimelineChart from "@/core/components/charts/TimelineChart.vue";
import {
  dashboardRemoteDataSource,
  eventsRemoteDataSource,
} from "@/core/models/global-variables";
import { useRouter } from "vue-router";
import { onMounted, ref } from "vue";
import { endOfMonth, getTime, startOfMonth } from "date-fns";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import { EventState } from "@/modules/events/models/event-state";
import { DashboardOccupancyModel } from "@/modules/dashboard/models/dashboard-occupancy-model";
import CustomInput from "@/core/components/CustomInput.vue";
import CustomDatePicker from "@/core/components/CustomDatePicker.vue";
import CustomDropDown from "@/core/components/CustomDropDown.vue";

//Filter
import CustomCleanFilterButton from "@/core/components/filter/CustomCleanFilterButton.vue";
import CustomApplyFilterButton from "@/core/components/filter/CustomApplyFilterButton.vue";
import AppliedFilterBox from "@/core/components/filter/AppliedFilterBox.vue";
import FilterBox from "@/core/components/filter/FilterBox.vue";
import FilterState from "@/core/components/filter/FilterState.vue";
import FilterTag from "@/core/components/filter/FilterTag.vue";
import { pt } from "date-fns/locale";
import { RouteName } from "@/core/models/route-name";
import { Utils } from "@/core/models/utils";

const { t, locale } = useI18n();
const { showError } = useToast();
const router = useRouter();
const occupancyDashboardData = ref<DashboardOccupancyModel | null>(null);
const eventTypes = ref<string[]>([]);
const isLoading = ref<boolean>(true);
const loadingChart = ref<boolean>(false);
const eventStates = EventState.events;

//Filter
const filterResourceId = ref<string>("");
const filterEventType = ref<{ id: number; value: string } | null>(null);
const filterLocation = ref<string>("");
const filterStartDate = ref<Date | null>(new Date());
const filterEndDate = ref<Date | null>(endOfMonth(filterStartDate.value!));
const filterState = ref<string[]>([]);
const appliedFilters = ref<{
  resourceId: string;
  eventType: string;
  location: string;
  startDate: string;
  endDate: string;
  state: EventState[];
}>({
  resourceId: "",
  eventType: "",
  startDate: Utils.formatDate(filterStartDate.value!, locale.value),
  endDate: Utils.formatDate(filterEndDate.value!, locale.value),
  state: [],
  location: "",
});

onMounted(() => {
  loadInitialData();
});

async function loadInitialData() {
  isLoading.value = true;
  try {
    const startOfMonthSeconds = filterStartDate.value
      ? getTime(startOfMonth(filterStartDate.value))
      : undefined;
    const endOfMonthSeconds = filterEndDate.value
      ? getTime(endOfMonth(filterEndDate.value))
      : undefined;
    const [occupancyRes, eventTypesRes] = await Promise.all([
      dashboardRemoteDataSource.getOccupancyData({
        startDate: startOfMonthSeconds,
        endDate: endOfMonthSeconds,
      }),
      eventsRemoteDataSource.getEventsTypes(),
    ]);
    if (!occupancyRes.isSuccess) {
      showError({
        message: occupancyRes.detail!,
        title: occupancyRes.title,
        statusCode: occupancyRes.statusCode,
      });
    }
    occupancyDashboardData.value = occupancyRes.data;

    if (!eventTypesRes.isSuccess) {
      showError({
        message: eventTypesRes.detail!,
        title: eventTypesRes.title,
        statusCode: eventTypesRes.statusCode,
      });
    }
    eventTypes.value = eventTypesRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function applyFilters() {
  try {
    loadingChart.value = true;
    const response = await dashboardRemoteDataSource.getOccupancyData({
      resourceId: filterResourceId.value,
      eventType: filterEventType.value?.value,
      location: filterLocation.value,
      startDate: filterStartDate.value?.getTime(),
      endDate: filterEndDate.value?.getTime(),
      eventStatus: filterState.value,
    });
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    appliedFilters.value = {
      resourceId: filterResourceId.value,
      eventType: filterEventType.value?.value ?? "",
      location: filterLocation.value,
      startDate: filterStartDate.value
        ? Utils.formatDate(filterStartDate.value!, locale.value)
        : "",
      endDate: filterEndDate.value
        ? Utils.formatDate(filterEndDate.value!, locale.value)
        : "",
      state: filterState.value.map((item) =>
        EventState.getEventStateByValue(item),
      ),
    };
    occupancyDashboardData.value = response.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    loadingChart.value = false;
  }
}

async function clearFilters() {
  loadingChart.value = true;

  try {
    const response = await dashboardRemoteDataSource.getOccupancyData({});
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    occupancyDashboardData.value = response.data;
    appliedFilters.value = {
      resourceId: "",
      eventType: "",
      location: "",
      startDate: "",
      endDate: "",
      state: [],
    };

    filterResourceId.value = "";
    filterEventType.value = null;
    filterLocation.value = "";
    filterStartDate.value = null;
    filterEndDate.value = null;
    filterState.value = [];
  } catch (error: any) {
    showError({ title: error });
  } finally {
    loadingChart.value = false;
  }
}

function hasActiveFilters(): boolean {
  return (
    appliedFilters.value.resourceId.length > 0 ||
    appliedFilters.value.eventType.length > 0 ||
    appliedFilters.value.location.length > 0 ||
    appliedFilters.value.startDate.length > 0 ||
    appliedFilters.value.endDate.length > 0 ||
    appliedFilters.value.state.length > 0
  );
}

function hasUnappliedFilters(): boolean {
  const fieldsChanged =
    appliedFilters.value.resourceId !== filterResourceId.value ||
    appliedFilters.value.eventType !== (filterEventType.value?.value ?? "") ||
    appliedFilters.value.location !== filterLocation.value ||
    appliedFilters.value.startDate !==
      (filterStartDate.value
        ? Utils.formatDate(filterStartDate.value!, locale.value)
        : "") ||
    appliedFilters.value.endDate !==
      (filterEndDate.value
        ? Utils.formatDate(filterEndDate.value!, locale.value)
        : "");

  const stateChanged = !arraysEqualUnordered(
    filterState.value,
    appliedFilters.value.state.map((item) => item.value),
  );

  return fieldsChanged || stateChanged;
}

function arraysEqualUnordered(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
}

function getActiveFiltersCount(): number {
  return Object.values(appliedFilters.value).filter((value) => value.length > 0)
    .length;
}

function go(name: string, params: Record<string, any> = {}) {
  router.push({
    name,
    params,
  });
}

function buildTooltipHtml(
  eventId: string,
  quantity: number,
  startDate: Date,
  endDate: Date,
): string {
  return `
    <div style="font-size: 12px; line-height: 1.6;">
      <div><strong>${t("occupation.timeline.tooltip.name")}:</strong> ${eventId}</div>
       <div><strong>${t("occupation.timeline.tooltip.duration")}:</strong> ${Utils.formatDate(startDate, locale.value)} → ${Utils.formatDate(endDate, locale.value)}</div>
      <div><strong>${t("occupation.timeline.tooltip.quantity")}:</strong> ${quantity}</div>
    </div>
  `;
}
</script>
